import {
  Box,
  FormControl,
  Button,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
} from "@mui/material";
import React, { ReactElement } from "react";
import { FormContainer, TextFieldElement, useWatch } from "react-hook-form-mui";
import { GameIcon, getAllSets } from "../../controls/IconSet";
import { Game } from "../../models";
import { useTranslation } from "react-i18next";
import SettingsIcon from "@mui/icons-material/Settings";
import PaletteIcon from "@mui/icons-material/Palette";
import SchoolIcon from "@mui/icons-material/School";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";

interface EditGameFormProps {
  game: Game;

  updateGame: (data: {
    title?: string;
    description?: string;
    iconSet?: number;
    expertMode?: boolean;
  }) => void;
  hideEdit: () => void;
  hideSubmitButton?: boolean;
}

interface FormValues {
  title?: string;
  description?: string;
  iconSet?: number;
  expertMode?: boolean;
}
// Inner component that has access to form context
function EditGameFormInner(props: {
  updateGame: (data: {
    title?: string;
    description?: string;
    iconSet?: number;
    expertMode?: boolean;
  }) => void;
  hideSubmitButton: boolean;
  iconSet: number;
  expertMode: boolean;
  defaultTitle?: string | null;
  defaultDescription?: string | null;
}) {
  const {
    updateGame,
    hideSubmitButton,
    iconSet,
    expertMode,
    defaultTitle,
    defaultDescription,
  } = props;

  // Watch form values in real-time when submit button is hidden
  const title = useWatch({ name: "title" });
  const description = useWatch({ name: "description" });

  // Update parent in real-time when submit button is hidden
  React.useEffect(() => {
    if (hideSubmitButton) {
      const finalIconSet = iconSet === 0 ? null : iconSet;
      const updates: any = {
        iconSet: finalIconSet as any,
        expertMode: expertMode,
      };

      // Use watched value if available, otherwise use default
      // This ensures we always have a value to send
      if (title !== undefined) {
        updates.title = title;
      } else if (defaultTitle !== undefined) {
        updates.title = defaultTitle;
      }

      if (description !== undefined) {
        updates.description = description;
      } else if (defaultDescription !== undefined) {
        updates.description = defaultDescription;
      }

      updateGame(updates);
    }
  }, [
    title,
    description,
    iconSet,
    expertMode,
    hideSubmitButton,
    updateGame,
    defaultTitle,
    defaultDescription,
  ]);

  return null;
}

export function EditGameFormControl(props: EditGameFormProps) {
  const { game, updateGame, hideEdit, hideSubmitButton = false } = props;
  const { t } = useTranslation();
  const { themeConfig } = useAppTheme();
  const updateGameName = (data: FormValues) => {
    console.log("💾 Saving game settings", {
      data,
      currentIconSet: iconSet,
      willSaveAs: iconSet === 0 ? "null (Default Stones)" : iconSet,
    });
    // iconSet = 0 means use default theme stones (null)
    // iconSet > 0 means use that icon set
    const finalIconSet = iconSet === 0 ? null : iconSet;
    console.log(
      "💾 Final iconSet value:",
      finalIconSet,
      "type:",
      typeof finalIconSet
    );
    updateGame({
      ...data,
      iconSet: finalIconSet as any,
      expertMode: expertMode,
    });
    if (!hideSubmitButton) {
      hideEdit();
    }
  };

  // iconSet = 0 means "use default theme stones"
  // iconSet = 1-22 means "use icon set"
  const initSet = game.iconSet != null && game.iconSet > 0 ? game.iconSet : 0;
  const [iconSet, setIconSet] = React.useState(initSet);
  const [expertMode, setExpertMode] = React.useState(game.expertMode || false);

  // Update iconSet state when game.iconSet changes
  React.useEffect(() => {
    const newIconSet =
      game.iconSet != null && game.iconSet > 0 ? game.iconSet : 0;
    console.log("🔄 IconSet state updated from game:", {
      gameIconSet: game.iconSet,
      newIconSet,
    });
    setIconSet(newIconSet);
  }, [game.iconSet]);

  const [menuItems] = React.useState<ReactElement[]>(() => {
    let items = [];

    // Option 0: Default Theme Stones (no icons)
    items.push(
      <MenuItem key="menuItem-default" value="0">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PaletteIcon sx={{ fontSize: 20, color: "primary.main" }} />
          <Typography fontWeight={600}>
            {t("GameEditForm.default_stones_label")}
          </Typography>
        </Box>
      </MenuItem>
    );

    // Options 1-22: Icon Sets
    for (let i = 1; i <= getAllSets().length; i++) {
      const stoneSet = i;
      items.push(
        <MenuItem key={`menuItem-${i}`} value={String(stoneSet)}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GameIcon stoneSet={stoneSet} color={1} sx={{ mr: 1 }} />
            <GameIcon stoneSet={stoneSet} variant={1} sx={{ mx: 1 }} />
            <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
              #{i}
            </Typography>
          </Box>
        </MenuItem>
      );
    }
    return items;
  });

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = Number(event.target.value);
    console.log("🎨 IconSet changed:", {
      from: iconSet,
      to: newValue,
      label:
        newValue === 0 ? "Default (Theme Stones)" : `Icon Set #${newValue}`,
    });
    setIconSet(newValue);
    // If submit button is hidden, update immediately
    if (hideSubmitButton) {
      const finalIconSet = newValue === 0 ? null : newValue;
      updateGame({
        iconSet: finalIconSet as any,
        expertMode: expertMode,
      });
    }
  };

  const toggleExpertMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    console.log("expert mode is: ", newValue);
    setExpertMode(newValue);
    // If submit button is hidden, update immediately
    if (hideSubmitButton) {
      const finalIconSet = iconSet === 0 ? null : iconSet;
      updateGame({
        iconSet: finalIconSet as any,
        expertMode: newValue,
      });
    }
  };

  return (
    <Box>
      <FormContainer
        onSuccess={updateGameName}
        defaultValues={{
          title: game.title || "",
          description: game.description || "",
        }}
      >
        <EditGameFormInner
          updateGame={updateGame}
          hideSubmitButton={hideSubmitButton}
          iconSet={iconSet}
          expertMode={expertMode}
          defaultTitle={game.title ?? undefined}
          defaultDescription={game.description ?? undefined}
        />
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Game Info Section */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 1 }}
            >
              {t("GameEditForm.game_info")}
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextFieldElement
                label={t("GameEditForm.title_label")}
                autoFocus={true}
                id="title"
                defaultValue={game.title}
                name="title"
                placeholder={
                  game.title || t("CreateGameDefaults.title", { count: 1 })
                }
                fullWidth
                variant="filled"
                sx={{
                  "& .MuiFilledInput-root": {
                    bgcolor: "action.hover",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: "action.selected",
                      borderColor: "text.secondary",
                    },
                    "&.Mui-focused": {
                      bgcolor: "action.selected",
                      borderColor: "primary.main",
                    },
                    "&:before, &:after": {
                      display: "none",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "text.secondary",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiFilledInput-input": {
                    color: "primary.main",
                  },
                }}
              />
              <TextFieldElement
                label={t("GameEditForm.description_label")}
                id="description"
                name="description"
                placeholder={
                  game.description ||
                  t("CreateGameDefaults.description", { count: 1 })
                }
                fullWidth
                multiline
                rows={2}
                variant="filled"
                sx={{
                  "& .MuiFilledInput-root": {
                    bgcolor: "action.hover",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: "action.selected",
                      borderColor: "text.secondary",
                    },
                    "&.Mui-focused": {
                      bgcolor: "action.selected",
                      borderColor: "primary.main",
                    },
                    "&:before, &:after": {
                      display: "none",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "text.secondary",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiFilledInput-input": {
                    color: "primary.main",
                  },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ borderColor: "text.secondary" }} />

          {/* Stone Set Section */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PaletteIcon sx={{ fontSize: 20, color: "primary.main" }} />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="primary.main"
                sx={{ textTransform: "uppercase", letterSpacing: 1 }}
              >
                {t("GameEditForm.appearance")}
              </Typography>
            </Box>
            <FormControl fullWidth variant="filled">
              <InputLabel
                sx={{
                  color: "text.secondary",
                  "&.Mui-focused": { color: "primary.main" },
                }}
              >
                {t("GameEditForm.choose_stoneset_label")}
              </InputLabel>
              <Select
                value={String(iconSet)}
                label={t("GameEditForm.choose_stoneset_label")}
                onChange={handleChange}
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "action.selected",
                    borderColor: "text.secondary",
                  },
                  "&.Mui-focused": {
                    bgcolor: "action.selected",
                    borderColor: "primary.main",
                  },
                  "&:before, &:after": {
                    display: "none",
                  },
                  "& .MuiSelect-icon": {
                    color: "primary.main",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "background.paper",
                      border: "2px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      "& .MuiMenuItem-root": {
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "text.secondary",
                        },
                        "&.Mui-selected": {
                          bgcolor: "text.secondary",
                          "&:hover": {
                            bgcolor: "text.secondary",
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {menuItems.map((m) => m)}
              </Select>
              <FormHelperText sx={{ color: "text.secondary" }}>
                {t("GameEditForm.stoneset_helper")}
              </FormHelperText>
            </FormControl>
          </Box>

          <Divider sx={{ borderColor: "text.secondary" }} />

          {/* Expert Mode Section */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SchoolIcon sx={{ fontSize: 20, color: "primary.main" }} />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="primary.main"
                sx={{ textTransform: "uppercase", letterSpacing: 1 }}
              >
                {t("GameEditForm.game_mode")}
              </Typography>
              <Tooltip
                title={t("GameEditForm.expert_mode_info")}
                placement="top"
              >
                <IconButton size="small" sx={{ p: 0.5 }}>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <Card
              variant="outlined"
              sx={{
                bgcolor: expertMode ? themeConfig.accent.light : "action.hover",
                borderColor: expertMode
                  ? themeConfig.accent.border
                  : "text.secondary",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <FormControlLabel
                  disabled={game.gameHasStarted || false}
                  control={
                    <Switch
                      checked={expertMode}
                      onChange={toggleExpertMode}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: themeConfig.accent.primary,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            bgcolor: themeConfig.accent.primary,
                          },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color={
                          game.gameHasStarted
                            ? "text.secondary"
                            : "primary.main"
                        }
                      >
                        {t("GameEditForm.expert_mode_label")}
                      </Typography>
                      {expertMode && (
                        <Chip
                          label={t("GameEditForm.active")}
                          size="small"
                          sx={{
                            mt: 0.5,
                            bgcolor: themeConfig.accent.background,
                            color: themeConfig.accent.primary,
                            border: `1px solid ${themeConfig.accent.primary}`,
                          }}
                        />
                      )}
                    </Box>
                  }
                />
                {game.gameHasStarted && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {t("GameEditForm.expert_mode_info")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {!hideSubmitButton && (
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              sx={{
                mt: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontWeight: 700,
                py: 1.5,
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              {t("Buttons.save")}
            </Button>
          )}
        </Box>
      </FormContainer>
    </Box>
  );
}
